/**
 * Live exchange rates for the cost-of-living calculator.
 *
 * Source: Frankfurter (https://api.frankfurter.dev) — free, no API key, no
 * documented rate limit, sourced from European Central Bank reference rates
 * updated once per banking day (~16:00 CET). It's the most reliable free
 * option available: dedicated "cost of living" APIs are either paid
 * (Numbeo) or low-quality RapidAPI mirrors; a plain FX API plus our own
 * curated cost dataset is the honest, dependency-safe choice.
 *
 * Rates are cached in localStorage for 24h (matching Frankfurter's own
 * update cadence — refetching more often just re-reads the same numbers).
 * If the fetch fails (offline, API down) or hasn't resolved yet, callers
 * fall back to the last-known static rates baked into costOfLiving.json.
 */

const FRANKFURTER_URL = 'https://api.frankfurter.dev/v1/latest?base=USD';
const CACHE_KEY = 'aditas_fx_rates_v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

// AED is a hard peg to USD maintained by the UAE central bank since 1997
// (3.6725 AED = 1 USD) — it never floats, and Frankfurter's ECB-sourced
// rates don't cover it, so this is a correct constant, not a stale guess.
const AED_PER_USD = 3.6725;

export type ExchangeRates = Record<string, number>; // 1 unit of currency = N USD

interface RateCache {
  rates: ExchangeRates;
  fetchedAt: number;
}

function readCache(): RateCache | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RateCache;
    if (!parsed?.rates || typeof parsed.fetchedAt !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(cache: RateCache): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Storage full/unavailable (private browsing) - live rates just won't
    // persist across reloads, which is fine, not fatal.
  }
}

/**
 * Fetches live USD-relative exchange rates, using a 24h localStorage cache.
 * Returns null if no live rates are available (never fetched successfully
 * and no usable cache) — callers should fall back to static rates.
 */
export async function fetchLiveExchangeRates(): Promise<ExchangeRates | null> {
  const cached = readCache();
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.rates;
  }

  try {
    const response = await fetch(FRANKFURTER_URL);
    if (!response.ok) throw new Error(`Frankfurter responded ${response.status}`);
    const payload = (await response.json()) as { base: string; rates: Record<string, number> };

    // Frankfurter returns "units of CURRENCY per 1 USD"; our schema stores
    // the reciprocal ("1 unit of CURRENCY = X USD") to match costOfLiving.json.
    const rates: ExchangeRates = { USD: 1 };
    for (const [code, perUsd] of Object.entries(payload.rates)) {
      if (perUsd > 0) rates[code] = 1 / perUsd;
    }
    rates.AED = 1 / AED_PER_USD;

    writeCache({ rates, fetchedAt: Date.now() });
    return rates;
  } catch {
    // Network error, CORS, or Frankfurter down - serve the stale cache if
    // we have one (better than nothing), otherwise let the caller fall
    // back to the static snapshot.
    return cached?.rates ?? null;
  }
}
